/**
 * The client-side filter behind the Offer Locations List (10.02.12 § AC 5).
 * Pure string work: the page ships every Standort in its HTML, so a search
 * never reaches the network.
 */

/** Characters typed before the list starts filtering. */
export const SEARCH_MIN_LENGTH = 3;

/** Lower-cased and stripped of accents, so `koln` reaches `Köln`. */
function fold(text: string): string {
  return text.toLowerCase().normalize('NFD').replaceAll(/[̀-ͯ]/g, '');
}

/**
 * The German spelling-out of the umlauts, so `koeln` reaches `Köln` too —
 * typing `oe` for `ö` is as common as dropping the dots, and `fold` alone
 * turns `Köln` into `koln`, which `koeln` does not contain.
 */
function spellOut(text: string): string {
  return text
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .replaceAll('ß', 'ss');
}

/**
 * Everything one Standort can be found by, as one folded string: the name a
 * visitor sees, its town and its postcode. Both umlaut spellings are indexed
 * because the query carries only one of them.
 */
export function searchHaystack(location: {
  displayName: string;
  city?: string;
  postcode?: string;
}): string {
  const parts = [location.displayName, location.city, location.postcode]
    .filter((part): part is string => !!part)
    .join(' ');
  return `${fold(parts)} ${fold(spellOut(parts))}`;
}

/** The query as it is compared against a haystack; `''` means "do not filter". */
export function searchTerm(query: string): string {
  const trimmed = query.trim();
  return trimmed.length >= SEARCH_MIN_LENGTH ? fold(trimmed) : '';
}
